"use client";

import { Role } from "@prisma/client";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { signOut, useSession } from "next-auth/react";
import React, { use, useEffect } from "react";
import Confirm from "@/app/(components)/generics/Confirm/Confirm";
import Logo from "@/app/(components)/generics/Logo/Logo";
import PageLoader from "@/app/(components)/generics/loaders/PageLoader/PageLoader";
import Modal from "@/app/(components)/generics/Modal/Modal";
import Header from "@/app/(components)/specifics/navigation/Header/Header";
import Sidebar from "@/app/(components)/specifics/navigation/Sidebar/Sidebar";
import { useFetchUser } from "@/app/hooks/fetchs/user/useFetchUser";
import { initCrispChat } from "@/app/libs/CrispChat";
import { SidebarStore } from "@/app/stores/SidebarStore";
import { UserStore } from "@/app/stores/UserStore";
import { UStyles } from "@/app/utils/UStyles";
import { RouteSegments, Routes } from "@/app/vars/Routes";
import styles from "./AppWrapper.module.css";

const AUTH_PAGES = [
  RouteSegments.FORGOT_PASSWORD,
  RouteSegments.RESET_PASSWORD,
  RouteSegments.CONFIRM_EMAIL,
];
const ADMIN_ROUTES = [RouteSegments.USERS];

type Props = Readonly<{
  children: React.ReactNode;
  params: Promise<{ lang: string }>;
}>;

export default function AppWrapperContent(props: Props) {
  const { children } = props;
  const params = use(props.params);
  const lang = params.lang;

  const isOpen = SidebarStore((state) => state.isOpen);

  const { data: session, status } = useSession();
  const pathname = usePathname();
  const router = useRouter();

  const isLoginRoute = pathname.endsWith(RouteSegments.LOGIN);
  const isAuthPage = AUTH_PAGES.some((route) => pathname.endsWith(route));
  const isPublicRoute = isLoginRoute || isAuthPage;
  const isAdminRoute = ADMIN_ROUTES.some((route) => pathname.endsWith(route));
  const isConfiguratorRoute = pathname.endsWith(RouteSegments.CONFIGURATOR);
  const sidebarOpen = isConfiguratorRoute ? false : isOpen;

  const { isLoading: isUserLoading } = useFetchUser(status === "authenticated" && !isPublicRoute);

  const user = UserStore((state) => state.user);

  const searchParams = useSearchParams();
  const emailParam = searchParams.get("e");
  const secretKeyParam = searchParams.get("k");

  useEffect(() => {
    if (user) {
      initCrispChat();
    }
  }, [user]);

  useEffect(() => {
    if (status === "loading") return;

    if (isLoginRoute && status === "authenticated") {
      router.replace(Routes.ROOT(lang));
      return;
    }

    if (isPublicRoute) return;

    if (status === "unauthenticated") {
      if (emailParam && secretKeyParam) return;
      router.replace(Routes.LOGIN(lang));
      return;
    }

    if (status === "authenticated" && !isUserLoading && !user) {
      signOut({ callbackUrl: Routes.LOGIN(lang) });
      return;
    }

    if (user && !user.isEmailVerified && !isPublicRoute) {
      signOut({ callbackUrl: Routes.LOGIN(lang) });
      return;
    }

    if (isAdminRoute && user && user.role !== Role.ADMIN) {
      router.replace(Routes.ROOT(lang));
    }
  }, [
    status,
    isUserLoading,
    user,
    router,
    isPublicRoute,
    isAdminRoute,
    isLoginRoute,
    secretKeyParam,
    lang,
    emailParam,
  ]);

  if (isLoginRoute) {
    return <>{children}</>;
  }

  if (isAuthPage) {
    return (
      <main className={styles.container}>
        <div className={styles.publicHeader}>
          <Logo />
        </div>
        <div className={styles.publicContent}>{children}</div>
      </main>
    );
  }

  if (status === "authenticated" && isUserLoading) {
    return <PageLoader params={params} />;
  }

  if (status === "loading" || status === "unauthenticated" || !session?.user || !user) {
    return null;
  }

  return (
    <main className={styles.container}>
      <Header />
      <Sidebar params={params} />
      <Modal />
      <Confirm params={params} />

      <div
        className={UStyles.classNames(
          styles.content,
          user ? (sidebarOpen ? styles.sidebarIsOpen : styles.sidebarIsClosed) : "",
        )}
      >
        {children}
      </div>
    </main>
  );
}
